import apiClient from '@/api/client'
import { toE164PhoneNumber } from '@/utils/validation'
import type {
  ProfileType,
  GetRegistrationSurveyResponse,
  SubmitRegistrationRequest,
  SubmitRegistrationResponse,
  RegistrationApiError,
} from '@/types/registration'

// Raw API response type that handles different response formats from the backend
interface RawRegistrationResponse {
  // Standard nested format
  success?: boolean
  message?: string
  user?: {
    id: string
    phone_number: string
    email?: string
    first_name: string
    last_name: string
  }
  profile?: {
    id: string
    type: ProfileType
  }
  // Flat format (alternative API response)
  id?: string
  user_id?: string
  phone_number?: string
  email?: string
  first_name?: string
  last_name?: string
  profile_id?: string
  profile_type?: ProfileType
  /** Beneficiary profile id (prefer for order beneficiary PATCH / listings) */
  beneficiary_profile_id?: string
  beneficiary_id?: string
}

// =============================================================================
// Error Codes
// =============================================================================

export const REGISTRATION_ERROR_CODES = {
  DUPLICATE_PHONE_NUMBER: 'DUPLICATE_PHONE_NUMBER',
  DUPLICATE_EMAIL: 'DUPLICATE_EMAIL',
  INVALID_USER_DATA: 'INVALID_USER_DATA',
  SURVEY_NOT_FOUND: 'SURVEY_NOT_FOUND',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  SERVER_ERROR: 'SERVER_ERROR',
} as const

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Extract a human-readable message from the details object.
 * Returns all field-level errors joined, e.g. "Phone number: user with this phone number already exists."
 */
function formatDetailsErrors(details: Record<string, string[]>): string | null {
  const entries = Object.entries(details).filter(
    ([, msgs]) => Array.isArray(msgs) && msgs.length > 0,
  )
  if (entries.length === 0) return null

  return entries
    .map(([field, msgs]) => {
      const label = field.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
      return `${label}: ${msgs.join(', ')}`
    })
    .join('\n')
}

/**
 * Parse and format API error messages
 */
function parseApiError(error: any, fallbackMessage: string): string {
  const errorData = error.response?.data as RegistrationApiError
  
  // Handle specific error codes
  if (errorData?.error_code) {
    switch (errorData.error_code) {
      case REGISTRATION_ERROR_CODES.DUPLICATE_PHONE_NUMBER:
        return 'This phone number is already registered'
      case REGISTRATION_ERROR_CODES.DUPLICATE_EMAIL:
        return 'This email is already registered'
      case REGISTRATION_ERROR_CODES.INVALID_USER_DATA:
      case 'INVALID_PROFILE_DATA':
      case REGISTRATION_ERROR_CODES.VALIDATION_ERROR: {
        if (errorData.details) {
          const detailMessage = formatDetailsErrors(errorData.details)
          if (detailMessage) return detailMessage
        }
        return errorData.error || 'Invalid registration data'
      }
      case REGISTRATION_ERROR_CODES.SURVEY_NOT_FOUND:
        return 'Registration form not available'
      default:
        return errorData.error || fallbackMessage
    }
  }
  
  // Handle generic error message
  if (errorData?.error) {
    return errorData.error
  }
  
  // Handle DRF-style validation errors (field: [errors])
  if (error.response?.data && typeof error.response.data === 'object') {
    const data = error.response.data
    // Check for field-level errors
    const fieldKeys = Object.keys(data).filter(k => Array.isArray(data[k]))
    if (fieldKeys.length > 0) {
      const firstField = fieldKeys[0]
      const firstError = data[firstField][0]
      if (typeof firstError === 'string') {
        return `${firstField}: ${firstError}`
      }
    }
    // Check for non_field_errors
    if (data.non_field_errors && Array.isArray(data.non_field_errors)) {
      return data.non_field_errors[0]
    }
    // Check for detail field
    if (data.detail) {
      return data.detail
    }
  }
  
  // Handle network errors
  if (error.code === 'ECONNABORTED') {
    return 'Request timed out. Please try again.'
  }
  
  if (!error.response) {
    return 'Network error. Please check your connection.'
  }
  
  return fallbackMessage
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Fetch the dynamic registration survey for a profile type
 * GET /api/surveys/registration/{profileType}/
 * 
 * @param profileType - The type of profile to register (client, beneficiary, provider)
 * @returns Survey configuration and questions
 */
export async function getRegistrationSurvey(
  profileType: ProfileType
): Promise<GetRegistrationSurveyResponse> {
  console.log('[registrationApi] Fetching survey for profile type:', profileType)
  
  try {
    const response = await apiClient.get<GetRegistrationSurveyResponse>(
      `/api/surveys/registration/${profileType}/`,
      { timeout: 10000 } // 10 second timeout
    )
    
    console.log('[registrationApi] Survey fetched successfully:', {
      surveyId: response.data.survey?.id,
      questionCount: response.data.questions?.length || 0,
    })
    
    return response.data
  } catch (error: any) {
    console.error('[registrationApi] Failed to fetch survey:', error)
    throw new Error(parseApiError(error, 'Failed to fetch registration survey'))
  }
}

/**
 * Register a new client (new user + client profile)
 * POST /api/users/register/
 * Use this for "Add Client" — creates a new user account and client profile.
 * Do not use POST /api/users/profiles/ for new clients (that endpoint is for
 * adding a profile to the already logged-in user; one client profile per user).
 *
 * @param data - Registration request with profile_type 'client' and answers (including password_confirm)
 * @returns Created user and profile information
 */
export async function registerClient(
  data: SubmitRegistrationRequest
): Promise<SubmitRegistrationResponse> {
  const answers = data.answers as Record<string, unknown>

  // Resolve phone_number: prefer explicit field, fall back to survey answer codes
  let phone_number = toE164PhoneNumber(String(answers.phone_number ?? ''))
  if (!phone_number || phone_number.replace(/\D/g, '').length < 10) {
    for (const [key, value] of Object.entries(answers)) {
      const k = key.toUpperCase()
      if ((k === 'CONTACT1' || k === 'PHONE' || k === 'MOBILE') && value) {
        const candidate = toE164PhoneNumber(String(value))
        if (candidate && candidate.replace(/\D/g, '').length >= 10) {
          phone_number = candidate
          break
        }
      }
    }
  }

  const email = answers.email ? String(answers.email) : undefined
  const first_name = String(answers.first_name ?? '')
  const last_name = String(answers.last_name ?? '')
  const password = String(answers.password ?? '').trim()
  const password_confirm = String(answers.password_confirm ?? '').trim()
  const includePassword = password.length > 0 || password_confirm.length > 0

  const profile_data: Record<string, unknown> = {
    date_of_birth: answers.date_of_birth ? String(answers.date_of_birth) : undefined,
    gender: answers.gender ? String(answers.gender) : undefined,
    preferred_language: answers.preferred_language ? String(answers.preferred_language) : 'en',
    id_number: String(answers.id_number ?? ''),
  }

  // Include all survey-specific answers (keyed by question code) in profile_data
  const mappedFieldKeys = new Set([
    'phone_number', 'email', 'first_name', 'last_name',
    'password', 'password_confirm', 'date_of_birth',
    'gender', 'preferred_language', 'id_number',
  ])
  for (const [key, value] of Object.entries(answers)) {
    if (!mappedFieldKeys.has(key) && value !== undefined && value !== '') {
      profile_data[key] = value
    }
  }

  const cleanedProfileData = Object.fromEntries(
    Object.entries(profile_data).filter(([_, v]) => v !== undefined && v !== '')
  )

  const body: Record<string, unknown> = {
    phone_number,
    email,
    first_name,
    last_name,
    ...(includePassword ? { password, password_confirm } : {}),
    profile_data: cleanedProfileData,
    survey_id: data.survey_id !== 'default' ? data.survey_id : undefined,
  }
  const cleanedBody = Object.fromEntries(
    Object.entries(body).filter(([_, v]) => v !== undefined && v !== '')
  ) as Record<string, unknown>
  if (cleanedBody.profile_data && typeof cleanedBody.profile_data === 'object') {
    cleanedBody.profile_data = Object.fromEntries(
      Object.entries(cleanedBody.profile_data as Record<string, unknown>).filter(
        ([_, v]) => v !== undefined && v !== ''
      )
    )
  }

  console.log('[registrationApi] Registering new client via POST /api/users/register/:', {
    phone_number: cleanedBody.phone_number,
    has_profile_data: !!cleanedBody.profile_data,
  })

  try {
    const response = await apiClient.post<RawRegistrationResponse>(
      '/api/users/register/',
      cleanedBody,
      { timeout: 60000 }
    )

    const responseData = response.data as RawRegistrationResponse
    const normalizedResponse: SubmitRegistrationResponse = {
      success: responseData.success ?? true,
      message: responseData.message ?? 'Registration successful',
      user: responseData.user ?? {
        id: responseData.id ?? responseData.user_id ?? '',
        phone_number: responseData.phone_number ?? phone_number,
        email: responseData.email ?? email,
        first_name: responseData.first_name ?? first_name,
        last_name: responseData.last_name ?? last_name,
      },
      profile: responseData.profile ?? {
        id: responseData.profile_id ?? responseData.id ?? '',
        type: 'client',
      },
    }
    return normalizedResponse
  } catch (error: any) {
    console.error('[registrationApi] Client registration failed:', {
      status: error.response?.status,
      errorCode: error.response?.data?.error_code,
      error: error.response?.data?.error,
    })
    throw new Error(parseApiError(error, 'Registration failed. Please try again.'))
  }
}

/**
 * Submit registration via the survey-based flow (beneficiary/provider).
 * POST /api/surveys/registration/submit/
 * Creates User + Profile + ProfileOnboarding + SurveyResponse in one transaction.
 * For beneficiaries this also creates the ClientBeneficiary link.
 *
 * @param data - Registration request data including survey answers, client_id, relationship_type
 * @returns Created user and profile information
 */
export async function submitRegistration(
  data: SubmitRegistrationRequest
): Promise<SubmitRegistrationResponse> {
  console.log('[registrationApi] Submitting registration:', {
    profileType: data.profile_type,
    surveyId: data.survey_id,
    hasClientId: !!data.client_id,
    relationshipType: data.relationship_type,
  })

  try {
    const answers = data.answers as Record<string, unknown>

    let phone_number = toE164PhoneNumber(String(answers.phone_number ?? ''))
    if (!phone_number || phone_number.replace(/\D/g, '').length < 10) {
      for (const [key, value] of Object.entries(answers)) {
        const k = key.toUpperCase()
        if ((k === 'CONTACT1' || k === 'PHONE' || k === 'MOBILE') && value) {
          const candidate = toE164PhoneNumber(String(value))
          if (candidate && candidate.replace(/\D/g, '').length >= 10) {
            phone_number = candidate
            break
          }
        }
      }
    }

    const body: Record<string, unknown> = {
      survey_id: data.survey_id !== 'default' ? data.survey_id : undefined,
      profile_type: data.profile_type,
      answers: {
        ...answers,
        phone_number: phone_number || answers.phone_number,
      },
    }

    if (data.client_id) body.client_id = data.client_id
    if (data.business_client_profile_id) {
      body.business_client_profile_id = data.business_client_profile_id
    }
    if (data.relationship_type) body.relationship_type = data.relationship_type

    const cleanedBody = Object.fromEntries(
      Object.entries(body).filter(([_, v]) => v !== undefined)
    )

    console.log('[registrationApi] Sending to /api/surveys/registration/submit/:', {
      profile_type: cleanedBody.profile_type,
      survey_id: cleanedBody.survey_id,
      has_client_id: !!cleanedBody.client_id,
    })

    const response = await apiClient.post<RawRegistrationResponse>(
      '/api/surveys/registration/submit/',
      cleanedBody,
      { timeout: 60000 }
    )

    const responseData = response.data as RawRegistrationResponse
    const beneficiaryProfileId =
      responseData.profile?.id ??
      responseData.profile_id ??
      responseData.beneficiary_profile_id ??
      responseData.beneficiary_id

    const normalizedResponse: SubmitRegistrationResponse = {
      success: responseData.success ?? true,
      message: responseData.message ?? 'Registration successful',
      user: responseData.user ?? {
        id: responseData.id ?? responseData.user_id ?? '',
        phone_number: responseData.phone_number ?? phone_number ?? '',
        email: responseData.email ?? String(answers.email ?? ''),
        first_name: responseData.first_name ?? String(answers.first_name ?? ''),
        last_name: responseData.last_name ?? String(answers.last_name ?? ''),
      },
      profile: responseData.profile ?? {
        id: String(beneficiaryProfileId ?? ''),
        type: responseData.profile_type ?? data.profile_type,
      },
    }
    return normalizedResponse
  } catch (error: any) {
    console.error('[registrationApi] Registration failed:', {
      status: error.response?.status,
      errorCode: error.response?.data?.error_code,
      error: error.response?.data?.error,
      details: error.response?.data,
    })
    throw new Error(parseApiError(error, 'Registration failed. Please try again.'))
  }
}

/**
 * Check if a phone number is already registered
 * GET /api/users/check-phone/{phoneNumber}/
 * 
 * @param phoneNumber - Phone number to check
 * @returns Whether the phone number exists
 */
export async function checkPhoneExists(phoneNumber: string): Promise<boolean> {
  try {
    const response = await apiClient.get<{ exists: boolean }>(
      `/api/users/check-phone/${encodeURIComponent(phoneNumber)}/`
    )
    return response.data.exists
  } catch (error) {
    // If endpoint doesn't exist or errors, assume phone doesn't exist
    console.warn('[registrationApi] Phone check failed, assuming not exists')
    return false
  }
}

/**
 * Check if an email is already registered
 * GET /api/users/check-email/{email}/
 * 
 * @param email - Email to check
 * @returns Whether the email exists
 */
export async function checkEmailExists(email: string): Promise<boolean> {
  try {
    const response = await apiClient.get<{ exists: boolean }>(
      `/api/users/check-email/${encodeURIComponent(email)}/`
    )
    return response.data.exists
  } catch (error) {
    // If endpoint doesn't exist or errors, assume email doesn't exist
    console.warn('[registrationApi] Email check failed, assuming not exists')
    return false
  }
}

// =============================================================================
// Dynamic dropdown options (model-backed surveys)
// =============================================================================

/**
 * One option returned by ``/api/surveys/registration/dynamic-options/``.
 * The same shape regardless of the source model (logistics.Area, system.Tag, ...).
 */
export interface DynamicOption {
  value: string
  label: string
  label_en?: string
  label_ar?: string
}

interface DynamicOptionsResponse {
  success?: boolean
  options: DynamicOption[]
}

/**
 * Fetch the live option list for a model-backed dropdown question.
 *
 * @param snapshotId Latest snapshot id (returned by getRegistrationSurvey).
 * @param questionCode The code of the question with an option_source (e.g. 'city').
 * @param currentAnswers Running form answers — required for cascading
 *   dropdowns (e.g. neighborhood depends on the chosen city).
 * @param language ISO code; localised label is returned in `.label`.
 * @param signal Optional AbortSignal so callers can cancel stale fetches.
 */
export async function getRegistrationDynamicOptions(
  snapshotId: string,
  questionCode: string,
  currentAnswers: Record<string, unknown> = {},
  language: 'en' | 'ar' = 'en',
  signal?: AbortSignal,
  search?: string,
  limit?: number,
): Promise<DynamicOption[]> {
  const response = await apiClient.post<DynamicOptionsResponse>(
    '/api/surveys/registration/dynamic-options/',
    {
      snapshot_id: snapshotId,
      question_code: questionCode,
      current_answers: currentAnswers,
      language,
      ...(search ? { search } : {}),
      ...(limit ? { limit } : {}),
    },
    { signal },
  )
  return response.data.options || []
}

// =============================================================================
// Service Export
// =============================================================================

export const registrationService = {
  getSurvey: getRegistrationSurvey,
  getDynamicOptions: getRegistrationDynamicOptions,
  submitRegistration,
  registerClient,
  checkPhoneExists,
  checkEmailExists,
}
